import { find, propEq } from 'ramda'
import { AutomationTypes } from './automation-services'

export enum AutomationHandler {
  // Message
  SendEmailMessage = 'Handlers::EmailHandler',
  SendPushNotification = 'Handlers::MobilePushHandler',
  SendSMSMessage = 'Handlers::SMSHandler',

  SendToZapier = 'Handlers::ZapierHandler',

  // HTTP
  SendHTTPPost = 'Handlers::WebhookHandler',

  // Contract
  SendContract = 'Handlers::ContractHandler',
  SendDocument = 'Handlers::DocumentHandler',
  SendForm = 'Handlers::FormHandler',

  // Inquiry
  BookingMarkFollowUp = 'Handlers::Inquiry::FollowUpHandler',
  BookingUnmarkFollowUp = 'Handlers::Inquiry::UnFollowHandler',
  BookingAddTagHandler = 'Handlers::Inquiry::AddTagHandler',
  BookingRemoveTagHandler = 'Handlers::Inquiry::RemoveTagHandler',
  BookingMarkPaid = 'Handlers::Inquiry::MarkPaidHandler',
  BookingMarkUnpaid = 'Handlers::Inquiry::MarkUnPaidHandler',
  BookingMarkCheckedOut = 'Handlers::Inquiry::CheckedOutHandler',
  BookingMarkCheckedIn = 'Handlers::Inquiry::CheckedInHandler',
  BookingConfirmHandler = 'Handlers::Inquiry::ConfirmHandler',
  BookingCreateTaskHandler = 'Handlers::Inquiry::CreateTaskHandler',
  BookingRemoveTaskHandler = 'Handlers::Inquiry::RemoveTaskHandler',

  // Hold Event
  CalendarCreateHoldEvent = 'Handlers::Inquiry::HoldEventHandler',

  // Expense
  ExpenseCreateHandler = 'expense',
  ExpenseMarkPaidHandler = 'Handlers::Expense::MarkPaidHandler',
  ExpenseMarkUnpaidHandler = 'Handlers::Expense::MarkUnpaidHandler',
  ExpenseNotifyGuestHandler = 'Handlers::Expense::NotifyGuestHandler',

  // Device
  DeviceNotifyGuestHandler = 'Handlers::Lock::NotifyGuestHandler',

  // Extra
  ExtraNotifyGuestHandler = 'Handlers::Extra::NotifyGuestHandler',

  // Guest
  GuestWhatsappWelcomeHandler = 'Handlers::Inquiry::WhatsAppInitialHandler',

  // Incident
  IncidentCreateHandler = 'Handlers::Lock::CreateIncidentHandler',
  IncidentNotifyGuestHandler = 'Handlers::Incident::NotifyGuestHandler',
  IncidentCreateTaskHandler = 'Handlers::Incident::CreateTaskHandler',

  // Inventory
  InventoryCreateTaskHandler = 'Handlers::Inventory::CreateTaskHandler',

  // Rental
  RentalMarkReadyHandler = 'Handlers::Rental::MarkReadyHandler',
  RentalMarkNotReadyHandler = 'Handlers::Rental::MarkNotReadyHandler',

  // Invoice
  InvoiceMarkPaidHandler = 'Handlers::Invoice::MarkPaidHandler',
  InvoiceChargeHandler = 'Handlers::Invoice::ChargeHandler',
  InvoiceSendToGuestHandler = 'Handlers::Invoice::SendToGuestHandler',
  InvoiceCancelBookingHandler = 'Handlers::Invoice::CancelBookingHandler',

  // Task
  TaskCreateHandler = 'task',
  TaskNotifyAssignee = 'Handlers::Task::NotifyAssigneeHandler',
  TaskNotifyOldAssignee = 'Handlers::Task::NotifyOldAssigneeHandler',
  TaskNotifyCreator = 'Handlers::Task::NotifyCreatorHandler',
  TaskMarkIncidentResolved = 'Handlers::Task::MarkIncidentResolvedHandler',

  // Review
  SendReview = 'Handlers::Review::SendAIReviewHandler',
  ReviewRespond = 'Handlers::Review::RespondHandler',
  ReviewDraftAIReview = 'Handlers::Review::DraftAIReviewHandler',
  ReviewSendAIReview = 'Handlers::Review::SendAIReviewHandler',

  // Security Deposit
  SecurityDepositCreateDeposit = 'Handlers::SecurityDeposit::CreateDepositHandler',

  // Message
}

export interface AutomationHandlerItem {
  title: string
  handler: AutomationHandler
  tooltip?: string
  icon?: string
  color?: string
  types?: AutomationTypes[]
}

export const automationHandlers: AutomationHandlerItem[] = [
  {
    handler: AutomationHandler.SendEmailMessage,
    title: $localize`Send Email Message`,
    icon: 'cs-icon cs-icon-send-email',
    color: 'text-danger',
    types: [AutomationTypes.Message],
  },
  {
    handler: AutomationHandler.SendHTTPPost,
    title: $localize`Send HTTP Request`,
    icon: 'cs-icon cs-icon-http',
    color: 'text-success',
    types: [AutomationTypes.Message],
  },
  {
    handler: AutomationHandler.SendPushNotification,
    title: $localize`Send Push Notification`,
    icon: 'cs-icon cs-icon-send-notification',
    color: 'text-warning',
    types: [AutomationTypes.Message],
  },
  {
    handler: AutomationHandler.SendSMSMessage,
    title: $localize`Send SMS Message`,
    icon: 'cs-icon cs-icon-send-sms',
    color: 'text-info',
    types: [AutomationTypes.Message],
  },
  {
    title: $localize`Send Contract`,
    handler: AutomationHandler.SendContract,
    icon: 'cs-icon cs-icon-file-signature text-success',
    tooltip: $localize`A contract is a legal template which requires the recipient(s) to sign and fill in requested fields. Contracts can contain text boxes, checkboxes, and data dictionary codes.`,
    types: [AutomationTypes.Document],
  },
  {
    title: $localize`Send Form`,
    handler: AutomationHandler.SendForm,
    icon: 'cs-icon cs-icon-file-alt2 text-info',
    tooltip: $localize`A form is a data-retrieval template that will allow the recipient(s) to fill in requested fields that will populate the guest details in your AdvanceCM account. Forms can contain textboxes, checkboxes, and data dictionary codes.`,
    types: [AutomationTypes.Document],
  },
  {
    title: $localize`Send Printout`,
    handler: AutomationHandler.SendDocument,
    icon: 'cs-icon cs-icon-pdf text-danger',
    tooltip: $localize`A printout is a read-only, electronic template containing fields that are already filled with data from your AdvanceCM account. Documents can contain data dictionary codes.`,
    types: [AutomationTypes.Document],
  },
  {
    title: $localize`Mark For Follow-up`,
    handler: AutomationHandler.BookingMarkFollowUp,
    icon: 'fas fa-star text-warning',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Remove Follow-up Flag`,
    handler: AutomationHandler.BookingUnmarkFollowUp,
    icon: 'far fa-star text-warning',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Add Booking Tag`,
    handler: AutomationHandler.BookingAddTagHandler,
    icon: 'fas fa-tags text-info',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Remove Booking Tag`,
    handler: AutomationHandler.BookingRemoveTagHandler,
    icon: 'far fa-tags text-info',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Mark as Paid`,
    handler: AutomationHandler.BookingMarkPaid,
    icon: 'cs-icon cs-icon-dollar-circle-check text-indigo',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Mark as Unpaid`,
    handler: AutomationHandler.BookingMarkUnpaid,
    icon: 'cs-icon cs-icon-dollar-circle-times text-indigo',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Mark as Checked In`,
    handler: AutomationHandler.BookingMarkCheckedIn,
    icon: 'cs-icon cs-icon-checkin text-success',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Mark as Checked Out`,
    handler: AutomationHandler.BookingMarkCheckedOut,
    icon: 'cs-icon cs-icon-checkout text-danger',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Create Hold Event`,
    handler: AutomationHandler.CalendarCreateHoldEvent,
    icon: 'cs-icon cs-icon-calendar-pause text-danger',
    types: [AutomationTypes.Booking],
  },
  {
    title: $localize`Confirm Booking`,
    handler: AutomationHandler.BookingConfirmHandler,
    types: [],
  },
  {
    title: $localize`Create Task`,
    handler: AutomationHandler.BookingCreateTaskHandler,
    types: [],
  },
  {
    title: $localize`Remove all Booking Tasks`,
    handler: AutomationHandler.BookingRemoveTaskHandler,
    types: [],
  },
  {
    title: $localize`Create Expense`,
    handler: AutomationHandler.ExpenseCreateHandler,
    types: [],
  },
  {
    title: $localize`Mark Expense as Paid`,
    handler: AutomationHandler.ExpenseMarkPaidHandler,
    types: [],
  },
  {
    title: $localize`Mark Expense as Unpaid`,
    handler: AutomationHandler.ExpenseMarkUnpaidHandler,
    types: [],
  },
  {
    title: $localize`Notify Guest`,
    handler: AutomationHandler.ExpenseNotifyGuestHandler,
    types: [],
  },
  {
    title: $localize`Notify Guest`,
    handler: AutomationHandler.DeviceNotifyGuestHandler,
    types: [],
  },
  {
    title: $localize`Notify Guest`,
    handler: AutomationHandler.ExtraNotifyGuestHandler,
    types: [],
  },
  {
    title: $localize`Create Incident`,
    handler: AutomationHandler.IncidentCreateHandler,
    types: [],
  },
  {
    title: $localize`Notify Guest`,
    handler: AutomationHandler.IncidentNotifyGuestHandler,
    types: [],
  },
  {
    title: $localize`Create Task from Incident`,
    handler: AutomationHandler.IncidentCreateTaskHandler,
    types: [],
  },
  {
    title: $localize`Mark Rental as Ready`,
    handler: AutomationHandler.RentalMarkReadyHandler,
    types: [],
  },
  {
    title: $localize`Mark Rental as Not Ready`,
    handler: AutomationHandler.RentalMarkNotReadyHandler,
    types: [],
  },
  {
    title: $localize`Charge Invoice`,
    handler: AutomationHandler.InvoiceChargeHandler,
    types: [],
  },
  {
    title: $localize`Mark Invoice as Paid`,
    handler: AutomationHandler.InvoiceMarkPaidHandler,
    types: [],
  },
  {
    title: $localize`Send Invoice to Guest`,
    handler: AutomationHandler.InvoiceSendToGuestHandler,
    types: [],
  },
  {
    title: $localize`Create Task`,
    handler: AutomationHandler.TaskCreateHandler,
    types: [],
  },
  {
    title: $localize`Notify Task Assignee`,
    handler: AutomationHandler.TaskNotifyAssignee,
    types: [],
  },
  {
    title: $localize`Notify Task Old Assignee`,
    handler: AutomationHandler.TaskNotifyOldAssignee,
    types: [],
  },
  {
    title: $localize`Notify Task Creator`,
    handler: AutomationHandler.TaskNotifyCreator,
    types: [],
  },
  {
    title: $localize`Mark Incident as Resolved`,
    handler: AutomationHandler.TaskMarkIncidentResolved,
    types: [],
  },
  {
    title: $localize`Send Review to Guest`,
    handler: AutomationHandler.SendReview,
    types: [],
  },
  {
    title: $localize`Respond to Guest Review`,
    handler: AutomationHandler.ReviewRespond,
    types: [],
  },
  {
    title: $localize`Draft AI Based Review`,
    handler: AutomationHandler.ReviewDraftAIReview,
    types: [],
  },
  {
    title: $localize`Send AI Based Review`,
    handler: AutomationHandler.ReviewSendAIReview,
    types: [],
  },
  {
    title: $localize`Create Security Deposit`,
    handler: AutomationHandler.SecurityDepositCreateDeposit,
    icon: 'cs-icon cs-icon-dollar-circle-plus-circle text-info',
    types: [],
  },
]

export function getAutomationHandlersByType(tag: AutomationTypes): AutomationHandlerItem[] {
  return automationHandlers.filter((h) => h.types?.includes(tag))
}

export function getAutomationHandler(value: AutomationHandler): AutomationHandlerItem | undefined {
  return find(propEq('handler', value), automationHandlers)
}
